import { Tabs as MuiTabs, TabsProps, tabsClasses } from "@mui/material";
import {
  ForwardedRef,
  MutableRefObject,
  ReactNode,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";

function mergeRefs<T>(...refs: Array<ForwardedRef<T> | undefined>) {
  return (value: T) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") ref(value);
      else if (ref) (ref as MutableRefObject<T | null>).current = value;
    });
  };
}

/**
 * Com #root { zoom }, o MUI Tabs mede o indicador via getBoundingClientRect
 * (px visuais) e aplica em left/width de layout — o pill fica deslocado.
 * Recalcula com offsetLeft/Width (layout, sem zoom).
 */
const ZoomAwareTabs = forwardRef<HTMLDivElement, TabsProps>(function ZoomAwareTabs(
  { orientation = "horizontal", value, children, ...other },
  ref
) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const syncIndicator = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;

    const indicator = root.querySelector<HTMLElement>(`.${tabsClasses.indicator}`);
    const selected = root.querySelector<HTMLElement>(`.${tabsClasses.flexContainer} .Mui-selected`);
    if (!indicator || !selected) return;

    if (orientation === "vertical") {
      const top = `${selected.offsetTop}px`;
      const height = `${selected.offsetHeight}px`;
      if (indicator.style.top !== top) indicator.style.top = top;
      if (indicator.style.height !== height) indicator.style.height = height;
      return;
    }

    const left = `${selected.offsetLeft}px`;
    const width = `${selected.offsetWidth}px`;
    if (indicator.style.left !== left) indicator.style.left = left;
    if (indicator.style.width !== width) indicator.style.width = width;
  }, [orientation]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    syncIndicator();
    const raf = requestAnimationFrame(syncIndicator);

    const resizeObserver = new ResizeObserver(syncIndicator);
    resizeObserver.observe(root);

    const indicator = root.querySelector(`.${tabsClasses.indicator}`);
    const mutationObserver = indicator ? new MutationObserver(syncIndicator) : null;
    if (indicator && mutationObserver) {
      mutationObserver.observe(indicator, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    window.addEventListener("resize", syncIndicator);
    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", syncIndicator);
    };
  }, [syncIndicator, value, children]);

  return (
    <MuiTabs ref={mergeRefs(rootRef, ref)} orientation={orientation} value={value} {...other}>
      {children as ReactNode}
    </MuiTabs>
  );
});

export default ZoomAwareTabs;
